import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, setToken } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loading from '../../components/common/Loading';

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [nameKana, setNameKana] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [valid, setValid] = useState(false);

  useEffect(() => {
    const checkInvitation = async () => {
      try {
        const data = await api.get<{ email: string }>(`/api/auth/invite/${token}`);
        setEmail(data.email);
        setValid(true);
      } catch {
        setError('この招待リンクは無効または期限切れです');
      } finally {
        setLoading(false);
      }
    };
    checkInvitation();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    
    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await api.post<{ message: string; token: string }>(`/api/auth/invite/${token}/accept`, { name, nameKana, password });
      if (response.token) {
        setToken(response.token);
        await refreshUser();
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'アカウント作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800">
        <Loading size="lg" message="招待を確認中..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800 p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">アカウント作成</h1>
            <p className="text-gray-500 text-sm">
              セカイエ業務システムへようこそ
            </p>
          </div>

          {!valid ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button variant="secondary" onClick={() => navigate('/login')}>
                ログイン画面へ
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {error}
                </div>
              )}

              <Input
                label="メールアドレス"
                type="email"
                value={email}
                disabled
              />

              <Input
                label="氏名"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="山田 太郎"
                required
              />

              <Input
                label="氏名（カナ）"
                value={nameKana}
                onChange={(e) => setNameKana(e.target.value)}
                placeholder="ヤマダ タロウ"
              />

              <Input
                label="パスワード"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="8文字以上、大文字・小文字・数字を含む"
                required
              />

              <Input
                label="パスワード（確認）"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="パスワードを再入力"
                required
              />

              <Button type="submit" className="w-full" loading={submitting}>
                アカウントを作成
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
